import React, { useMemo } from "react";
import { connect } from "react-redux";

import PathTypes from "types/PathTypes";
import ParkkiTheme from "themes/ParkkiTheme";

import MapPath from "components/MapPath";
import MapLabel from "components/MapLabel";

import MapUtils from "services/map/utils";

type Props = {
  loading: boolean;
  currentFloor: number;
  map: {
    initialized: boolean;
    paths: PathTypes[];
    selectedLayers: { id: string; ref_flow_paths?: [] }[];
  };
  filters: {
    from: number;
    to: number;
  };
  onSelectLayers: Function;
  getLabelIcon?: Function;
  getLabel?: Function;
  getBackground?: Function;
  defaultZoom: number;
  isLabelDisplayed?: Function;
  hasLabelBackground?: Function;
  isPathSelected?: Function;
  zoom: number;
  mapConfig: { labelsDisplayed?: boolean };
};

function PathsLayers({
  currentFloor,
  defaultZoom,
  map,
  getLabelIcon,
  getLabel,
  getBackground,
  loading,
  onSelectLayers,
  isLabelDisplayed,
  isPathSelected,
  hasLabelBackground,
  mapConfig,
  zoom,
}: Props): JSX.Element[] {
  const selectedLayers = useMemo(() => {
    return map?.selectedLayers || [];
  }, [map?.selectedLayers]);

  function handlePathClick(path: PathTypes) {
    const newSelectedLayer = {
      ...path,
      type: "path",
    };

    const alreadySelected = !!selectedLayers.find(
      (itm) => path?.id === itm?.id
    );

    const newSelectedLayers = alreadySelected
      ? selectedLayers.filter((itm) => path?.id !== itm?.id)
      : [...selectedLayers, newSelectedLayer];
    const isSelection = newSelectedLayers?.length > selectedLayers?.length;

    return onSelectLayers(newSelectedLayers, newSelectedLayer, isSelection);
  }

  function handleIsPathSelected(path: PathTypes): boolean {
    if (isPathSelected) {
      return isPathSelected(path);
    }

    return !!selectedLayers.find((layer) => {
      return layer?.id && layer?.id === path?.id;
    });
  }

  function handleIsLabelDisplayed(path: PathTypes) {
    if (isLabelDisplayed) {
      return (
        mapConfig?.labelsDisplayed &&
        isLabelDisplayed({ ...path, selected: handleIsPathSelected(path) })
      );
    }

    return mapConfig?.labelsDisplayed;
  }

  function handleHasLabelBackground(path: PathTypes) {
    if (hasLabelBackground) {
      return (
        mapConfig?.labelsDisplayed &&
        hasLabelBackground({ ...path, selected: handleIsPathSelected(path) })
      );
    }

    return true;
  }

  function ColorLuminance(hex: string, lum: number) {
    if (!hex?.startsWith("#")) {
      return hex;
    }

    // validate hex string
    hex = String(hex).replace(/[^0-9a-f]/gi, "");
    if (hex.length < 6) {
      hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    }
    lum = lum || 0;

    // convert to decimal and change luminosity
    var rgb = "#",
      c,
      i;
    for (i = 0; i < 3; i++) {
      c = parseInt(hex?.substr(i * 2, 2), 16);
      c = Math.round(Math.min(Math.max(0, c + c * lum), 255)).toString(16);
      rgb += ("00" + c)?.substr(c.length);
    }

    return rgb;
  }

  function getPathColor(path: PathTypes) {
    const background = getBackground
      ? getBackground(path)
      : path?.options?.background;

    if (path?.apiType === "TRAFFIC") {
      return background?.config?.mainColor;
    }

    let color =
      background?.config?.valueColors?.[91]?.backgroundColor ||
      path?.options?.selected_color;

    if (color === "white") {
      color = ParkkiTheme.colors.green;
    }
    return color;
  }

  function getArrowOptions(path: PathTypes) {
    const background = getBackground
      ? getBackground(path)
      : path?.options?.background;

    if (path?.apiType === "TRAFFIC") {
      return {
        ...(path?.options?.arrow || {}),
        selected_color: ColorLuminance(background?.config?.mainColor, -0.2),
      };
    }

    if (background?.config?.valueColors?.[91]?.backgroundColor !== undefined) {
      return {
        ...(path?.options?.arrow || {}),
        selected_color: ColorLuminance(getPathColor(path), -0.2),
      };
    }

    return {
      ...(path?.options?.arrow || {}),
    };
  }

  function handleIsInteractive(path: PathTypes) {
    return !["VIRTUAL", "UNEQUIPPED"].includes(path?.apiType);
  }

  if (!map?.initialized || loading) {
    return [];
  }

  return map?.paths
    ?.filter((path) =>
      currentFloor || currentFloor === 0
        ? path.floor_level === currentFloor
        : path
    )
    .map((path) => {
      const selected = handleIsPathSelected(path);
      const label = getLabel ? getLabel(path) : null;
      const pathColor = getPathColor(path);
      const isInteractive = handleIsInteractive(path);
      const background = getBackground
        ? getBackground(path)
        : path?.options?.background;

      return (
        <MapPath
          key={path.id}
          disabled={path?.type === "UNEQUIPPED" || path?.apiType === "VIRTUAL"}
          positions={path.positions}
          zoom={zoom}
          defaultZoom={defaultZoom}
          options={{
            ...(path?.options || {}),
            tooltipCoordinates: path?.display?.location,
            withLabel: handleIsLabelDisplayed(path),
            withLabelBackground: handleHasLabelBackground(path),
            background,
            selected_color: pathColor,
            arrow: {
              ...getArrowOptions(path),
            },
          }}
          onClick={() => handlePathClick(path)}
          selected={selected}
          label={
            label || getLabelIcon ? (
              <MapLabel layer={path} getIcon={getLabelIcon} label={label} />
            ) : null
          }
          interactive={isInteractive}
        />
      );
    });
}

const mapStateToProps = (state: any) => ({
  zoom: MapUtils.getZoom(state.Map),
  defaultZoom: MapUtils.getDefaultZoom(state.Map),
  currentFloor: MapUtils.getFloor(state.Map),
  flowFilters: state.Flow?.filters,
  mapConfig: state.Map.config,
});

// @ts-ignore
export default connect(mapStateToProps)(PathsLayers);
